import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { history } from '../lib/history.js';
import { selectDefaultLearnPath } from '../features/appVisibility/store/selectors.js';
import { configureStatusBar } from '../services/native/statusBarService.js';
import { exitNativeApp, listenForAppLifecycle, listenForAndroidBackButton } from '../services/native/appLifecycleService.js';
import { listenForDeepLinks } from '../services/native/deepLinkService.js';
import { listenForKeyboardChanges } from '../services/native/keyboardService.js';
import { getNetworkStatus } from '../services/native/networkService.js';
import { getPlatformClassName } from '../services/native/platformService.js';

const ROOT_TABS = new Set(['/courses', '/assessments', '/coaching', '/progress', '/library', '/account', '/unavailable']);
const GUEST_EXIT_ROUTES = new Set(['/signin']);

function getCurrentResolvedTheme() {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function isRootRoute() {
  return ROOT_TABS.has(history.location.pathname) || GUEST_EXIT_ROUTES.has(history.location.pathname);
}

function listenForNativeViewportChanges() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};

  const root = document.documentElement;
  const visualViewport = window.visualViewport;

  const update = () => {
    const heights = [
      window.innerHeight,
      visualViewport?.height,
    ].filter((height) => Number.isFinite(height) && height > 0);

    if (heights.length === 0) return;
    root.style.setProperty('--native-viewport-height', `${Math.round(Math.min(...heights))}px`);
  };

  update();
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);
  visualViewport?.addEventListener('resize', update);

  return () => {
    window.removeEventListener('resize', update);
    window.removeEventListener('orientationchange', update);
    visualViewport?.removeEventListener('resize', update);
    root.style.removeProperty('--native-viewport-height');
  };
}

export function NativeBoot() {
  const defaultLearnPath = useSelector(selectDefaultLearnPath);

  useEffect(() => {
    const platformClassName = getPlatformClassName();
    const platformClasses = platformClassName.split(' ');
    document.documentElement.classList.add(...platformClasses);
    const removeViewport = platformClasses.includes('platform-native')
      ? listenForNativeViewportChanges()
      : () => {};

    void configureStatusBar(getCurrentResolvedTheme());

    const removeDeepLinks = listenForDeepLinks(history);
    const removeLifecycle = listenForAppLifecycle((state) => {
      window.dispatchEvent(new CustomEvent('step:native-lifecycle', {
        detail: { state },
      }));
      if (state === 'resume' || state === 'foreground') {
        void configureStatusBar(getCurrentResolvedTheme());
        void getNetworkStatus();
      }
    });
    const removeKeyboard = listenForKeyboardChanges(({ open, height }) => {
      document.documentElement.classList.toggle('native-keyboard-open', open);
      document.documentElement.style.setProperty('--native-keyboard-height', `${height}px`);
    });
    const removeBackButton = listenForAndroidBackButton({
      history,
      isRootRoute,
      onRootBack: () => {
        if (history.location.pathname === defaultLearnPath || history.location.pathname === '/signin') {
          exitNativeApp();
          return;
        }
        history.push(defaultLearnPath);
      },
      onRecordingBack: () => {
        window.dispatchEvent(new CustomEvent('step:native-back-blocked', {
          detail: { reason: 'recording' },
        }));
      },
    });

    return () => {
      removeDeepLinks();
      removeLifecycle();
      removeKeyboard();
      removeBackButton();
      removeViewport();
      document.documentElement.classList.remove('native-keyboard-open');
      document.documentElement.style.removeProperty('--native-keyboard-height');
    };
  }, [defaultLearnPath]);

  return null;
}
